import { AsyncPipe } from "@angular/common";
import { Component, ViewChild, effect, inject, input } from "@angular/core";
import { GoogleMap, MapMarker } from "@angular/google-maps";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ForgeIconButtonModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { Utilities } from "@tylertech/shared-module";
import { ICoordinate, IMapViewMetadata, RepositoryDataCollection } from "@tylertech/tyler-api-types";
import { Observable } from "rxjs";
import { MapCallbackArgs } from "../interfaces/mapCallbackArgs";
import { GoogleMapsApiKeyLoaderService } from "../services/googleMapsApiKeyLoaderService";

enum RingOrientation
{
    Clockwise = 0,
    CounterClockwise
}

/**
 * ! TODO - Move this component to library/metadata after library/metadata is upgraded to Angular.
 */
@Component({
    selector: "metadata-map-view",
    templateUrl: "./metadataMapView.tmpl.html",
    styleUrls: [
        "./metadataMapView.scss"
    ],
    imports: [
        AsyncPipe,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeIconButtonModule,
        ForgeTooltipModule,
        GoogleMap,
        MapMarker,
    ]
})
export class MetadataMapViewComponent
{
    readonly model = input<any>();

    readonly datacontroller = input<RepositoryDataCollection>();

    readonly view = input<IMapViewMetadata>();

    readonly refreshToken = input(0);

    readonly getCenter = input<(args: MapCallbackArgs) => ICoordinate>();

    readonly getCoordinates = input<(args: MapCallbackArgs) => ICoordinate[]>();

    readonly recalculateMap = input<(args: MapCallbackArgs) => void>();

    @ViewChild("map")
    get map(): GoogleMap
    {
        return this._map;
    }

    set map(m: GoogleMap)
    {
        if (m && this._map !== m)
        {
            this._map = m;
            this.initMap();
        }
    }

    private _map: GoogleMap;
    private polygon: google.maps.Polygon;
    private drawingVertices: google.maps.LatLng[] = [];
    private previewPolyline: google.maps.Polyline;
    private cursorLine: google.maps.Polyline;
    private snapCircle: google.maps.Circle;
    private readonly snapThresholdPx = 10;
    isDrawing: boolean = false;
    apiLoaded: Observable<boolean>;
    loaded: boolean = false;

    constructor()
    {
        const googleMapsApiKeyLoaderService = inject(GoogleMapsApiKeyLoaderService);
        this.apiLoaded = googleMapsApiKeyLoaderService.IsLoaded(true);
        effect(() =>
        {
            this.refreshToken();
            this._centerDirty = true;
            if (this._map && this.loaded)
                this.setPolygon();
        });
    }

    private _centerDirty: boolean = true;
    private _mapCenter: google.maps.LatLngLiteral;

    get MapCenter(): google.maps.LatLngLiteral
    {
        if (this._mapCenter && !this._centerDirty)
            return this._mapCenter;

        this._centerDirty = false;

        //US Geographical Center
        let lat: number = 39.8283;
        let lng: number = -98.5795;

        if (this.view())
        {
            if (this.view().CenterProperty)
            {
                const center = this.getCenter()?.({ view: this.view(), model: this.model() });
                if (this.IsICoordinate(center))
                {
                    lat = center.Latitude;
                    lng = center.Longitude;
                }
            }
            if (this.view().Center)
            {
                lat = this.view().Center.Latitude;
                lng = this.view().Center.Longitude;
            }
            if (this.IsICoordinate(this.model()))
            {
                lat = this.model().Latitude;
                lng = this.model().Longitude;
            }
        }

        if (this._mapCenter && this._mapCenter.lat === lat && this._mapCenter.lng === lng)
            return this._mapCenter;

        this._mapCenter = <google.maps.LatLngLiteral>{
            lat: lat,
            lng: lng,
        };
        return this._mapCenter;
    }

    IsICoordinate(model: unknown): model is ICoordinate
    {
        return model
            && typeof model === "object"
            && "Latitude" in model
            && "Longitude" in model;
    }

    get Marker(): boolean
    {
        if (!this.view())
            return false;
        return !Utilities.IsNullOrUndefined(this.view().Marker) && this.view().Marker;
    }

    get Zoom(): number
    {
        return this.view()?.Zoom || 15;
    }

    get hasShape(): boolean
    {
        return !!this.polygon;
    }

    initMap(): void
    {
        if (this.view()?.CoordinatesProperty)
            this.setPolygon();
        this.loaded = true;
    }

    onDrawClick(): void
    {
        this.onClearShapeClick();
        this.drawingVertices = [];
        this.isDrawing = true;
        this.map.googleMap.setOptions({ disableDoubleClickZoom: true });
        this.previewPolyline = new google.maps.Polyline({ map: this.map.googleMap, strokeColor: "#00ff00", clickable: false });
        this.cursorLine = new google.maps.Polyline({ map: this.map.googleMap, strokeColor: "#00ff00", strokeOpacity: 0.5, clickable: false });
    }

    onOverlayClick(e: MouseEvent): void
    {
        const latLng = this.mouseEventToLatLng(e);
        if (!latLng) return;
        if (this.drawingVertices.length >= 2 && this.isNearOrigin(latLng))
        {
            this.finishDrawing();
            return;
        }
        this.drawingVertices.push(latLng);
        this.previewPolyline.setPath(this.drawingVertices);
        this.cursorLine.setPath([latLng, latLng]);
    }

    onOverlayDblClick(e: MouseEvent): void
    {
        e.stopPropagation();
        this.drawingVertices.pop();
        this.finishDrawing();
    }

    onOverlayMouseMove(e: MouseEvent): void
    {
        if (this.drawingVertices.length === 0) return;
        const latLng = this.mouseEventToLatLng(e);
        if (!latLng) return;
        const snapped = this.drawingVertices.length >= 2 && this.isNearOrigin(latLng)
            ? this.drawingVertices[0]
            : latLng;
        this.cursorLine.setPath([this.drawingVertices[this.drawingVertices.length - 1], snapped]);
    }

    private mouseEventToLatLng(e: MouseEvent): google.maps.LatLng | null
    {
        const projection = this.map.getProjection();
        if (!projection) return null;
        const mapDiv = this.map.googleMap.getDiv();
        const rect = mapDiv.getBoundingClientRect();
        const bounds = this.map.googleMap.getBounds();
        const ne = projection.fromLatLngToPoint(bounds.getNorthEast());
        const sw = projection.fromLatLngToPoint(bounds.getSouthWest());
        const x = sw.x + (e.clientX - rect.left) / rect.width * (ne.x - sw.x);
        const y = ne.y + (e.clientY - rect.top) / rect.height * (sw.y - ne.y);
        return projection.fromPointToLatLng(new google.maps.Point(x, y));
    }

    onDrawCancel(): void
    {
        this.finishDrawing(true);
    }

    private isNearOrigin(latLng: google.maps.LatLng): boolean
    {
        const origin = this.drawingVertices[0];
        const projection = this.map.getProjection();
        if (!projection) return false;
        const p1 = projection.fromLatLngToPoint(origin);
        const p2 = projection.fromLatLngToPoint(latLng);
        const zoom = this.map.googleMap.getZoom();
        const scale = Math.pow(2, zoom);
        const dx = (p2.x - p1.x) * scale;
        const dy = (p2.y - p1.y) * scale;
        return Math.sqrt(dx * dx + dy * dy) < this.snapThresholdPx;
    }

    private finishDrawing(clear: boolean = false): void
    {
        this.isDrawing = false;
        this.map.googleMap.setOptions({ disableDoubleClickZoom: false });
        this.previewPolyline?.setMap(null);
        this.previewPolyline = null;
        this.cursorLine?.setMap(null);
        this.cursorLine = null;
        this.snapCircle?.setMap(null);
        this.snapCircle = null;

        if (!clear && this.drawingVertices.length > 2)
        {
            this.polygon = new google.maps.Polygon({
                map: this.map.googleMap,
                paths: this.drawingVertices,
                strokeColor: "#00ff00",
                fillColor: "#333333",
                fillOpacity: 0.2,
                editable: true,
            });
        }
        this.drawingVertices = [];
    }

    setPolygon(): void
    {
        this.polygon?.setMap(null);
        const coordinates = this.getCoordinates()?.({ view: this.view(), model: this.model() });
        if (!coordinates?.length || !this.IsICoordinate(coordinates[0]))
            return;
        this.polygon = new google.maps.Polygon;

        const lngLatArray: google.maps.LatLngLiteral[] = coordinates.map(c =>
        {
            return <google.maps.LatLngLiteral>{
                lat: c.Latitude,
                lng: c.Longitude,
            };
        });

        this.polygon.setPaths(lngLatArray);
        this.polygon.setMap(this.map.googleMap);
    }

    onClearShapeClick(): void
    {
        if (this.isDrawing)
            this.finishDrawing(true);
        if (!this.polygon)
            return;
        this.polygon.setMap(null);
        this.polygon = null;
    }

    onSearchClick(): void
    {
        if (!this.polygon)
            return;
        this.recalculate();
    }

    private recalculate(): void
    {
        const path = this.polygon.getPath().getArray();
        if (path.length === 0)
            return;

        if (this.findRingOrientation(path) === RingOrientation.Clockwise)
            path.reverse();

        const newCoordinates: ICoordinate[] = [];
        path.forEach(p =>
        {
            newCoordinates.push({
                Latitude: p.lat(),
                Longitude: p.lng()
            });
        });

        this.model()[this.view().CoordinatesProperty] = newCoordinates;
        this.recalculateMap()?.({ view: this.view(), model: this.model(), datacontroller: this.datacontroller() });
    }

    private findRingOrientation(latLngPoints: google.maps.LatLng[]): RingOrientation
    {
        const points: google.maps.Point[] = latLngPoints
            .map(p => this.map.getProjection()
                .fromLatLngToPoint(p))
            .map(p => new google.maps.Point(p.x, -p.y));
        let sum: number = 0;
        for (let x = 0; x < points.length; x++)
        {
            const p1: google.maps.Point = points[x];
            const p2: google.maps.Point = points[(x + 1) % points.length];
            sum += (p2.x - p1.x) * (p2.y + p1.y);
        }
        return sum > 0 ? RingOrientation.Clockwise : RingOrientation.CounterClockwise;
    }
}
